import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Zap, Loader2, Cpu, Image } from 'lucide-react';
import api, { automationAPI } from '../../lib/api';

const PRIORITIES = [
  { value: 0, label: 'Low' },
  { value: 1, label: 'Medium' },
  { value: 2, label: 'High' },
];

const selectCls =
  'px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-blue-500';
const fmtUK = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

const FIELD_LABELS = {
  autopilot_enabled:            'Fetch autopilot',
  generation_autopilot_enabled: 'Generation autopilot',
  use_source_images:            'Source images',
};


function Switch({ label, hint, on, disabled, onChange, icon: Icon = Zap }) {
  return (
    <div className="flex items-start gap-2.5">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        disabled={disabled}
        onClick={onChange}
        className={`mt-0.5 relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 ${
          on ? 'bg-blue-600' : 'bg-slate-300'
        }`}
      >
        <span
          className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform"
          style={{ transform: on ? 'translateX(18px)' : 'translateX(4px)' }}
        />
      </button>
      <div className="min-w-0">
        <p className={`text-sm font-semibold flex items-center gap-1.5 ${on ? 'text-slate-900' : 'text-slate-500'}`}>
          <Icon size={13} className={on ? 'text-blue-600' : 'text-slate-300'} />
          {label}
        </p>
        <p className="text-xs text-slate-400 mt-0.5">{hint}</p>
      </div>
    </div>
  );
}


export default function SourceAutopilotCard({ source, onChange }) {
  const [models, setModels]       = useState([]);
  const [defaultId, setDefaultId] = useState(null);
  const [saving, setSaving]       = useState(null);

  useEffect(() => {
    api.get('/feeds/models')
      .then(({ data }) => {
        setModels(data.models || []);
        setDefaultId(data.default_id || null);
      })
      .catch(() => {});
  }, []);

  const save = async (field, value) => {
    setSaving(field);
    try {
      await automationAPI.updateSource(source.id, { [field]: value });
      toast.success(
        FIELD_LABELS[field] ? `${FIELD_LABELS[field]} ${value ? 'on' : 'off'}` : 'Saved'
      );
      // Reloads the source, so next_fetch_at reflects the change.
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(null);
    }
  };

  const fetchOn     = !!source.autopilot_enabled;
  const genOn       = !!source.generation_autopilot_enabled;
  const imgOn       = source.use_source_images !== false;
  const nextFetch   = fmtUK(source.next_fetch_at);
  const defaultLabel = models.find(m => m.id === defaultId)?.label;

  return (
    <div className={`border rounded-xl p-4 mb-5 shadow-sm transition-colors ${
      fetchOn || genOn ? 'bg-blue-50/50 border-blue-200' : 'bg-white border-slate-200'
    }`}>
      <div className="flex items-center gap-6 flex-wrap">
        <Switch
          label="Fetch"
          hint={fetchOn
            ? (nextFetch ? `Next fetch ${nextFetch}` : '')
            : ''}
          on={fetchOn}
          disabled={saving === 'autopilot_enabled' || !source.is_active}
          onChange={() => save('autopilot_enabled', !fetchOn)}
        />

        <Switch
          label="Generate"
          hint={genOn
            ? ''
            : ''}
          on={genOn}
          disabled={saving === 'generation_autopilot_enabled' || !source.is_active}
          onChange={() => save('generation_autopilot_enabled', !genOn)}
        />

        <Switch
          label="Source images"
          icon={Image}
          hint={imgOn
            ? 'Stories may publish the image supplied with the article'
            : 'Source images are never published — library or AI only'}
          on={imgOn}
          disabled={saving === 'use_source_images'}
          onChange={() => save('use_source_images', !imgOn)}
        />

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <label className="text-xs text-slate-400">Priority</label>
          <select
            value={source.priority ?? 0}
            disabled={saving === 'priority'}
            onChange={e => save('priority', parseInt(e.target.value, 10))}
            className={selectCls}
          >
            {PRIORITIES.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>

          <label className="text-xs text-slate-400 ml-2 flex items-center gap-1">
            <Cpu size={12} /> Model
          </label>
          <select
            value={source.default_model_id || ''}
            disabled={saving === 'default_model_id' || models.length === 0}
            onChange={e => save('default_model_id', e.target.value || null)}
            className={selectCls}
          >
            <option value="">
              {defaultLabel ? `Network default (${defaultLabel})` : 'Network default'}
            </option>
            {models.map(m => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>

          {saving && <Loader2 size={14} className="animate-spin text-slate-400" />}
        </div>
      </div>

      {(fetchOn || genOn) && !source.is_active && (
        <p className="text-xs text-amber-600 mt-3">
          This source is inactive, so autopilot will skip it until you switch it back on in Settings.
        </p>
      )}
    </div>
  );
}